// Phase 999.4 Plan 06 — Site Config view (R3 + D-16 + D-17 destination).
//
// Mounted at /admin/sites/:locationId/config. Fetches AdminSiteConfigOption[] from
// GET /api/admin/sites/{locationId}/config (Plan 02), renders reactstrap Table with
// the canonical D-16 column order (Option | Description | Current Value | Type | Source)
// plus a trailing Edit affordance per row.
//
// Source column (D-16): isOverridden ? <Badge color="primary">overridden</Badge>
//                                    : <Badge color="secondary">default</Badge>.
// Edit affordance: a Button that toggles the row into SiteConfigEdit (per-row inline
// edit). Save success refreshes that row in place via setRows; the parent owns the
// rows state so the edit child is fully controlled.
//
// Phase 260522-go3: Active/Inactive surfaces as a synthetic row at the top of the
// table (optionTypeId === ACTIVE_ROW_ID) so it sits alongside the real options.
// It's a real column on orglocation, not an orglocationdetails row — Edit on this
// row PUTs /api/admin/sites/{locationId}/active and refreshes local state. Hydrated
// from GET /api/admin/sites/{locationId} (lean single-site read; hardware-join-free
// so it works for sites that happen to have zero hardware rows — review WR-01).
//
// SECURITY: Bearer JWT in Authorization header; the real authorization gate is
// server-side `[Authorize(Policy = "GlobalAdmin")]` on the read endpoint (Plan 02).
// XSS: All cell content via React text-interpolation — no dangerouslySetInnerHTML.

import React, { useState, useCallback, useEffect } from 'react';
import { Container, Row, Col, Table, Badge, Button, Alert, Input } from 'reactstrap';
import { apiBase } from './apiBase';
import { SiteConfigEdit } from './SiteConfigEdit';
import { isReservedOption } from './reservedOptions';
import { SiteHardwareSection } from './SiteHardwareSection';

const COLUMNS = [
    { key: 'optionName',   name: 'Option' },
    { key: 'description',  name: 'Description' },
    { key: 'currentValue', name: 'Current Value' },
    { key: 'valueClass',   name: 'Type' },
    { key: 'source',       name: 'Source' },
    { key: 'actions',      name: '' },
];

// Sentinel id for the synthetic Active row. Real orglocationdetails rows always
// have positive optionTypeIds; -1 collides with nothing.
const ACTIVE_ROW_ID = -1;
const ACTIVE_DESCRIPTION = 'Show this site in admin dropdowns. Existing data stays accessible when off.';

function getAuthToken(auth) {
    if (auth && typeof auth.getAccessToken === 'function') {
        try { return auth.getAccessToken(); } catch (e) { /* server will 401 */ }
    }
    return '';
}

// Inline edit row for the Active flag — mirrors SiteConfigEdit's <tr> shape so
// the table layout stays stable, but PUTs to /sites/{id}/active instead of the
// generic options endpoint.
function ActiveEditRow({ isActive, locationId, auth, onSaved, onCancel }) {
    const [draft, setDraft]       = useState(String(isActive));
    const [busy, setBusy]         = useState(false);
    const [putError, setPutError] = useState(null);

    const onSaveClick = async () => {
        if (busy) return;
        const newValue = draft === 'true';
        if (newValue === isActive) { onCancel(); return; }
        setBusy(true);
        setPutError(null);
        try {
            const token = getAuthToken(auth);
            const res = await fetch(apiBase() + `/api/admin/sites/${locationId}/active`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: 'Bearer ' + token } : {}),
                },
                body: JSON.stringify({ IsActive: newValue }),
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error('HTTP ' + res.status + (text ? ': ' + text : ''));
            }
            onSaved(newValue);
        } catch (e) {
            setPutError(e && e.message ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <tr data-testid={`site-config-edit-${ACTIVE_ROW_ID}`}>
            <td>Active</td>
            <td className="text-muted small">{ACTIVE_DESCRIPTION}</td>
            <td>
                <Input
                    type="select"
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setPutError(null); }}
                    disabled={busy}
                    data-testid={`site-config-edit-input-${ACTIVE_ROW_ID}`}
                >
                    <option value="true">true</option>
                    <option value="false">false</option>
                </Input>
                {putError && (
                    <div className="text-danger small" data-testid={`site-config-edit-puterror-${ACTIVE_ROW_ID}`}>
                        {putError}
                    </div>
                )}
            </td>
            <td>Boolean</td>
            <td><span className="text-muted small">editing…</span></td>
            <td>
                <Button
                    color="primary"
                    size="sm"
                    onClick={onSaveClick}
                    disabled={busy}
                    data-testid={`site-config-save-${ACTIVE_ROW_ID}`}
                >
                    Save
                </Button>
                {' '}
                <Button
                    color="secondary"
                    size="sm"
                    onClick={onCancel}
                    disabled={busy}
                    data-testid={`site-config-cancel-${ACTIVE_ROW_ID}`}
                >
                    Cancel
                </Button>
            </td>
        </tr>
    );
}

export function SiteConfigView({ locationId, auth }) {
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [editingId, setEditingId] = useState(null);   // optionTypeId currently being edited (ACTIVE_ROW_ID for the Active row)
    const [isActive, setIsActive]   = useState(null);   // null = not yet loaded

    const fetchRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = getAuthToken(auth);
            const headers = token ? { Authorization: 'Bearer ' + token } : {};

            // Fetch config options and the lean single-site read in parallel.
            const [configRes, siteRes] = await Promise.all([
                fetch(apiBase() + `/api/admin/sites/${locationId}/config`, { method: 'GET', headers }),
                fetch(apiBase() + `/api/admin/sites/${locationId}`, { method: 'GET', headers }),
            ]);

            if (!configRes.ok) {
                const text = await configRes.text().catch(() => '');
                throw new Error('HTTP ' + configRes.status + (text ? ': ' + text : ''));
            }
            const data = await configRes.json();
            setRows(Array.isArray(data) ? data : []);

            if (siteRes.ok) {
                const site = await siteRes.json();
                if (site && typeof site.isActive === 'boolean') setIsActive(site.isActive);
            }
        } catch (e) {
            setError(e && e.message ? e.message : String(e));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [auth, locationId]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const onSaved = (optionTypeId, after) => {
        // Backend has upserted; reflect new currentValue + isOverridden in place.
        setRows((rs) => rs.map((r) =>
            r.optionTypeId === optionTypeId
                ? { ...r, currentValue: after, isOverridden: true }
                : r
        ));
        setEditingId(null);
    };

    const onSavedActive = (newValue) => {
        setIsActive(newValue);
        setEditingId(null);
    };

    // Synthetic Active row prepended to the real options rows. Hidden while
    // isActive is still loading so we don't briefly render a stale value.
    const allRows = isActive === null
        ? rows
        : [
            {
                optionTypeId: ACTIVE_ROW_ID,
                optionName: 'Active',
                description: ACTIVE_DESCRIPTION,
                valueClass: 'Boolean',
                defaultValue: 'true',
                currentValue: String(isActive),
                isOverridden: !isActive,  // non-default (i.e. inactive) → "overridden" badge
            },
            ...rows,
        ];

    return (
        <Container fluid data-testid="site-config-view">
            <Row className="mb-3 align-items-center">
                <Col><h2 className="mb-0">Site Config — LocationID {locationId}</h2></Col>
                <Col xs="auto">
                    <Button color="secondary" size="sm" tag="a" href="/sites">← Back to Sites</Button>
                </Col>
            </Row>
            {error && (
                <Alert color="danger" data-testid="site-config-error">
                    Failed to load config: {error}
                </Alert>
            )}
            <div data-testid="site-config-table">
                <Table size="sm" bordered hover responsive={false}>
                    <thead>
                        <tr>
                            {COLUMNS.map((c) => (
                                <th key={c.key} style={{ whiteSpace: 'nowrap' }}>{c.name}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {allRows.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length} className="text-center text-muted">
                                    {loading ? 'Loading...' : 'No options.'}
                                </td>
                            </tr>
                        ) : (
                            allRows.map((r) => {
                                if (editingId === r.optionTypeId) {
                                    if (r.optionTypeId === ACTIVE_ROW_ID) {
                                        return (
                                            <ActiveEditRow
                                                key={ACTIVE_ROW_ID}
                                                isActive={isActive}
                                                locationId={locationId}
                                                auth={auth}
                                                onSaved={onSavedActive}
                                                onCancel={() => setEditingId(null)}
                                            />
                                        );
                                    }
                                    return (
                                        <SiteConfigEdit
                                            key={r.optionTypeId}
                                            row={r}
                                            locationId={locationId}
                                            auth={auth}
                                            onSaved={(after) => onSaved(r.optionTypeId, after)}
                                            onCancel={() => setEditingId(null)}
                                        />
                                    );
                                }
                                return (
                                    <tr key={r.optionTypeId} data-testid={`site-config-row-${r.optionTypeId}`}>
                                        <td>{r.optionName}</td>
                                        <td className="text-muted small">{r.description}</td>
                                        <td><code>{r.currentValue ?? r.defaultValue ?? ''}</code></td>
                                        <td>{r.valueClass}</td>
                                        <td>
                                            {r.isOverridden
                                                ? <Badge color="primary">overridden</Badge>
                                                : <Badge color="secondary">default</Badge>}
                                        </td>
                                        <td>
                                            {isReservedOption(r.optionName) ? (
                                                <span
                                                    className="text-muted small"
                                                    data-testid={`site-config-reserved-${r.optionTypeId}`}
                                                >
                                                    reserved — future use
                                                </span>
                                            ) : (
                                                <Button
                                                    color="link"
                                                    size="sm"
                                                    onClick={() => setEditingId(r.optionTypeId)}
                                                    data-testid={`site-config-edit-btn-${r.optionTypeId}`}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>
            <Row className="mt-4">
                <Col>
                    <SiteHardwareSection locationId={locationId} auth={auth} />
                </Col>
            </Row>
        </Container>
    );
}

export default SiteConfigView;
